import { useState, useEffect, useCallback } from "react";
import { UserPlus, MapPin, X, Copy, Trash2, ShieldCheck, Pencil } from "lucide-react";
import { WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { C } from "./data";
import { Serif, Label, Avatar, Card } from "./ui";
import { getHousehold, addHouseholdMember, removeHouseholdMember, editHouseholdMember, linkMainMember } from "./consumerAuth";

export default function Dependents({ nav }) {
  const [hh, setHh] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mainCell, setMainCell] = useState("");
  const [linking, setLinking] = useState(false);

  const load = useCallback(() => { getHousehold().then(setHh).catch((e) => console.error("household load failed", e)); }, []);
  useEffect(() => { load(); }, [load]);

  const linkMain = async () => {
    if (!mainCell.trim()) return toast.error("Enter your main member's cell number");
    setLinking(true);
    try {
      const r = await linkMainMember(mainCell.trim());
      if (r.linked) { toast.success("Linked to your main member's cover"); setMainCell(""); load(); }
      else toast.success("We've saved it — you'll be linked as soon as your main member registers");
    } catch (e) {
      toast.error("Couldn't link account", { description: e.response?.data?.detail || e.message });
    } finally { setLinking(false); }
  };

  const main = hh?.main;
  const deps = hh?.dependents || [];
  const pending = hh?.pending_invites || [];
  const isOwner = hh?.is_owner;
  const plan = hh?.plan;
  const full = plan && plan.slots_left <= 0;

  const removeMember = async (d) => {
    const msg = d.activated
      ? `Remove ${d.full_name} from your cover? They'll keep their own ResQ1 login but will no longer be covered under your policy.`
      : `Remove ${d.full_name} from your cover?`;
    if (!window.confirm(msg)) return;
    try {
      await removeHouseholdMember(d.id);
      toast.success(`${d.full_name} removed from your cover`);
      load();
    } catch (e) { toast.error("Couldn't remove", { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div style={{ padding: "24px 22px 30px" }} data-testid="consumer-dependents">
      <Label>My household</Label>
      <Serif size={26} style={{ margin: "5px 0 4px" }}>Your <em style={{ color: C.slate }}>cover</em></Serif>
      <div style={{ fontSize: 12, color: C.slate, marginBottom: 16 }}>{main?.insurer ? `Protected via ${main.insurer}` : "ResQ1 Emergency Protection"}</div>

      {plan && (
        <Card style={{ marginBottom: 14, background: C.cream, display: "flex", alignItems: "center", gap: 12 }} data-testid="household-plan-card">
          <ShieldCheck size={20} color={C.navy} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }} data-testid="household-plan-label">{plan.label} plan</div>
            <div style={{ fontSize: 11.5, color: C.slate }} data-testid="household-plan-slots">
              {plan.used} of {plan.max_members} covered{plan.slots_left > 0 ? ` · ${plan.slots_left} space${plan.slots_left === 1 ? "" : "s"} left` : plan.grandfathered ? " · everyone on your old policy stays covered" : " · full"}
            </div>
          </div>
          {full && (
            <button onClick={() => nav("subscribe")} data-testid="household-upgrade-btn"
              style={{ padding: "8px 13px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Upgrade
            </button>
          )}
        </Card>
      )}

      <button onClick={() => nav("locate")} data-testid="dependents-locate"
        style={{ width: "100%", padding: 13, background: C.navy, color: "#fff", border: 0, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 18 }}>
        <MapPin size={16} /> See family on the map
      </button>

      <Card style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, borderColor: C.navy }} data-testid="dependents-owner">
        <Avatar name={main?.full_name || "You"} color={C.red} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{main?.full_name || "You"}</div>
          <div style={{ fontSize: 11, color: C.slate }}>{main?.role || "Main member"} · you{main?.cell ? ` · ${main.cell}` : ""}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.green }}>● active</span>
      </Card>

      {deps.map((d) => (
        <Card key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }} data-testid={`dependent-${d.id}`}>
          <Avatar name={d.full_name} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{d.full_name}</div>
            <div style={{ fontSize: 11, color: C.slate }}>{d.role}{d.activated ? " · on the app" : " · not on the app yet"}</div>
            {(d.cell || d.id_number) && (
              <div style={{ fontSize: 10.5, color: C.slate, marginTop: 2, fontFamily: "monospace" }} data-testid={`dependent-details-${d.id}`}>
                {d.cell || "no cell"}{d.id_number ? ` · ID ${d.id_number}` : " · no ID number"}
              </div>
            )}
          </div>
          {d.editable && (
            <button onClick={() => setEditing(d)} data-testid={`dependent-edit-${d.id}`} title="Fix their details"
              style={{ background: "none", border: 0, cursor: "pointer", color: C.navy, padding: 2, display: "flex" }}><Pencil size={15} /></button>
          )}
          {d.invite_link && !d.activated && (
            <a href={`https://wa.me/${(d.cell || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Join me on ResQ1 — you're on my cover and you'll carry your own panic button. Set up here: ${d.invite_link}`)}`}
              target="_blank" rel="noreferrer" data-testid={`dependent-share-${d.id}`} title="Send their link"
              style={{ display: "flex", alignItems: "center", color: "#25D366" }}><WhatsappLogo size={19} /></a>
          )}
          {d.self_added && !d.activated ? (
            <button onClick={() => removeMember(d)} data-testid={`dependent-remove-${d.id}`} title="Remove from cover"
              style={{ background: "none", border: 0, cursor: "pointer", color: C.slate, padding: 2, display: "flex" }}><Trash2 size={15} /></button>
          ) : (
            <>
              <span style={{ fontSize: 11, fontWeight: 600, color: d.sharing ? C.green : C.slate }}>{d.sharing ? "● sharing" : "○ private"}</span>
              {isOwner && (
                <button onClick={() => removeMember(d)} data-testid={`dependent-remove-${d.id}`} title="Remove from cover"
                  style={{ background: "none", border: 0, cursor: "pointer", color: C.slate, padding: 2, display: "flex" }}><Trash2 size={15} /></button>
              )}
            </>
          )}
        </Card>
      ))}

      {pending.map((p, i) => (
        <Card key={`p-${p.email}`} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, background: C.cream }} data-testid={`pending-invite-${i}`}>
          <Avatar name={p.name || p.email} color={C.amber} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name || p.email}</div>
            <div style={{ fontSize: 11, color: C.slate }}>{p.relationship ? `${p.relationship} · ` : ""}Invite sent</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.amber }}>● pending</span>
        </Card>
      ))}

      {deps.length === 0 && pending.length === 0 && (
        <div style={{ padding: 16, background: C.cream, borderRadius: 11, fontSize: 13, color: C.slate, marginBottom: 8 }} data-testid="dependents-empty">
          No family members yet. Invite your household so they carry the panic button and appear on your family map.
        </div>
      )}

      <button onClick={() => (full ? nav("subscribe") : setShowInvite(true))} data-testid="dependents-add"
        style={{ marginTop: 14, width: "100%", padding: 15, background: C.ink, color: "#fff", border: 0, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <UserPlus size={17} /> {full ? `${plan.label} plan is full — upgrade` : "Add a family member"}
      </button>
      {!full && (
        <div style={{ fontSize: 11.5, color: C.slate, textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
          They're covered the moment you add them. Send them the link and they'll carry their own panic button.
        </div>
      )}

      {isOwner && (
        <Card style={{ marginTop: 16, background: `${C.navy}0a`, borderColor: `${C.navy}22` }} data-testid="link-main-card">
          <Label style={{ marginBottom: 6 }}>On a family plan as a dependant?</Label>
          <div style={{ fontSize: 12.5, color: C.slate, lineHeight: 1.5, marginBottom: 10 }}>
            Enter the cellphone number of your <b>main member</b> to link your account to their cover.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={mainCell} onChange={(e) => setMainCell(e.target.value)} placeholder="Main member's cell, e.g. 27821234567" data-testid="link-main-input"
              style={{ flex: 1, minWidth: 0, padding: "11px 13px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, outline: "none", background: "#fff", color: C.ink }} />
            <button onClick={linkMain} disabled={linking} data-testid="link-main-submit"
              style={{ padding: "0 16px", background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: linking ? "default" : "pointer", opacity: linking ? 0.6 : 1, whiteSpace: "nowrap" }}>
              {linking ? "Linking…" : "Link"}
            </button>
          </div>
        </Card>
      )}

      {editing && <EditModal member={editing} onClose={() => setEditing(null)} onDone={(hhNext) => { setEditing(null); if (hhNext) setHh(hhNext); else load(); }} />}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function EditModal({ member, onClose, onDone }) {
  const [v, setV] = useState({
    full_name: member.full_name || "", cell: member.cell || "",
    email: member.email || "", relationship: member.role || "Dependant",
    id_number: member.id_number || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const save = async () => {
    if (v.id_number.replace(/\D/g, "").length && v.id_number.replace(/\D/g, "").length !== 13)
      return toast.error("A South African ID number is 13 digits");
    setBusy(true);
    try {
      const hhNext = await editHouseholdMember(member.id, v);
      toast.success(`${v.full_name.trim()}'s details updated`);
      onDone(hhNext);
    } catch (e) {
      toast.error("Couldn't save", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="edit-member-modal" style={{ position: "absolute", inset: 0, background: "rgba(11,30,59,0.55)", display: "flex", alignItems: "flex-end", zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", background: C.body, borderRadius: "18px 18px 0 0", padding: 22, maxHeight: "92%", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <Serif size={22}>Fix their details</Serif>
          <button onClick={onClose} data-testid="edit-member-close" style={{ background: "none", border: 0, cursor: "pointer", color: C.slate }}><X size={20} /></button>
        </div>
        <div style={{ fontSize: 12.5, color: C.slate, lineHeight: 1.5, marginBottom: 14 }}>
          Correct anything that came across wrong from your old policy — no need to remove them and add them again.
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <Fld label="Full name" value={v.full_name} onChange={set("full_name")} placeholder="Jane Doe" testid="edit-member-name" />
          <Fld label="Cell number" value={v.cell} onChange={set("cell")} placeholder="082 123 4567" testid="edit-member-cell" />
          <Fld label="ID number" value={v.id_number} onChange={set("id_number")} placeholder="13 digits" testid="edit-member-id" />
          <Fld label="Email (optional)" value={v.email} onChange={set("email")} placeholder="name@example.co.za" testid="edit-member-email" />
          <div>
            <Label style={{ marginBottom: 6 }}>Relationship</Label>
            <select value={v.relationship} onChange={set("relationship")} data-testid="edit-member-relationship"
              style={{ width: "100%", padding: "12px 14px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 14, background: "#fff", color: C.ink }}>
              {["Spouse", "Partner", "Son", "Daughter", "Parent", "Sibling", "Dependant", "Member", "Main member", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <button onClick={save} disabled={busy} data-testid="edit-member-save"
          style={{ marginTop: 18, width: "100%", padding: 14, background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Saving…" : "Save their details"}
        </button>
      </div>
    </div>
  );
}

function InviteModal({ onClose, onDone }) {
  const [v, setV] = useState({ full_name: "", cell: "", email: "", relationship: "Spouse" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // { link, name, cell, emailed }
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const submit = async () => {
    if (v.full_name.trim().length < 2) return toast.error("Enter their full name");
    if (!v.cell.trim() && !v.email.trim()) return toast.error("Add their cell number or email so we can send them the app link");
    setBusy(true);
    try {
      const r = await addHouseholdMember({
        full_name: v.full_name.trim(),
        cell: v.cell.trim() || undefined,
        email: v.email.trim() || undefined,
        relationship: v.relationship,
      });
      setSent({ link: r.invite_link, name: v.full_name.trim(), cell: r.cell, emailed: r.email_status === "sent" });
      toast.success(`${v.full_name.trim()} is covered`, { description: r.email_status === "sent" ? "We've emailed them the app link too" : "Send them the link to set up" });
    } catch (e) {
      toast.error("Couldn't add them", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  if (sent) {
    const msg = `Join me on ResQ1 Emergency Protection — you're on my cover, so you'll carry your own panic button and we can see each other on the family map. Set up here: ${sent.link}`;
    return (
      <div onClick={onClose} data-testid="invite-modal" style={{ position: "absolute", inset: 0, background: "rgba(11,30,59,0.55)", display: "flex", alignItems: "flex-end", zIndex: 50 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", background: C.body, borderRadius: "18px 18px 0 0", padding: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <Serif size={22}>{sent.name} is covered</Serif>
            <button onClick={onClose} data-testid="invite-close" style={{ background: "none", border: 0, cursor: "pointer", color: C.slate }}><X size={20} /></button>
          </div>
          <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.5, marginBottom: 16 }}>
            They're on your cover from right now.{sent.emailed ? " We've emailed them the link — " : " "}Send it on WhatsApp so they can set up their panic button in a minute.
          </div>
          <a href={`https://wa.me/${(sent.cell || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="invite-whatsapp"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: 15, background: "#25D366", color: "#0B1E3B", borderRadius: 999, fontSize: 15, fontWeight: 700, textDecoration: "none" }}>
            <WhatsappLogo size={19} /> Send on WhatsApp
          </a>
          <button onClick={() => { navigator.clipboard?.writeText(sent.link).then(() => toast.success("Invite link copied")).catch(() => toast.error("Couldn't copy — press and hold the link to copy it")); }} data-testid="invite-copy"
            style={{ marginTop: 10, width: "100%", padding: 14, background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}33`, borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Copy size={17} /> Copy invite link
          </button>
          <button onClick={onDone} data-testid="invite-done"
            style={{ marginTop: 14, width: "100%", padding: 12, background: "none", border: 0, color: C.slate, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} data-testid="invite-modal" style={{ position: "absolute", inset: 0, background: "rgba(11,30,59,0.55)", display: "flex", alignItems: "flex-end", zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", background: C.body, borderRadius: "18px 18px 0 0", padding: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Serif size={22}>Add to my cover</Serif>
          <button onClick={onClose} data-testid="invite-close" style={{ background: "none", border: 0, cursor: "pointer", color: C.slate }}><X size={20} /></button>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <Fld label="Their full name" value={v.full_name} onChange={set("full_name")} placeholder="Jane Doe" testid="invite-name" />
          <Fld label="Their cell number" value={v.cell} onChange={set("cell")} placeholder="082 123 4567" testid="invite-cell" />
          <Fld label="Their email (optional)" value={v.email} onChange={set("email")} placeholder="name@example.co.za" testid="invite-email" />
          <div>
            <Label style={{ marginBottom: 6 }}>Relationship</Label>
            <select value={v.relationship} onChange={set("relationship")} data-testid="invite-relationship"
              style={{ width: "100%", padding: "12px 14px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 14, background: "#fff", color: C.ink }}>
              {["Spouse", "Partner", "Son", "Daughter", "Parent", "Sibling", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="invite-submit"
          style={{ marginTop: 18, width: "100%", padding: 14, background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Adding…" : "Add them to my cover →"}
        </button>
      </div>
    </div>
  );
}

function Fld({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label style={{ marginBottom: 6 }}>{label}</Label>
      <input value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
        style={{ width: "100%", padding: "12px 14px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", color: C.ink }} />
    </div>
  );
}
